import { useEffect, useState } from "react";
import { SELECTED_CELL_RING } from "../utils/useGridSelection";
import { saveStatusRing, type CellStatus } from "../utils/useSaveStatus";

interface Props {
  initial: number;
  row: number;
  col: number;
  selected: boolean;
  status?: CellStatus;
  disabled?: boolean;
  // Format with thousands separators (monthly grid); daily cells stay plain.
  thousands?: boolean;
  // Live fill preview: while the anchor cell of a multi-selection is being
  // typed in, every other selected cell displays this value instead of its
  // own, so the user sees the whole range change as they type. Display-only —
  // nothing is saved until the anchor commits.
  preview: number | null;
  onPreview: (value: number | null) => void;
  // null = the user cleared the cell (distinct from typing 0).
  onCommit: (value: number | null, fillSelection: boolean) => void;
  onPasteRow: (text: string) => void;
  onSelectMouseDown: (shiftKey: boolean) => void;
}

// One editable quantity cell, shared by the Bottom Up monthly and daily grids.
export default function QtyCellInput({
  initial,
  row,
  col,
  selected,
  status,
  disabled,
  thousands,
  preview,
  onPreview,
  onCommit,
  onPasteRow,
  onSelectMouseDown,
}: Props) {
  const display = (n: number) => (thousands ? n.toLocaleString("en-US") : String(n));
  const [value, setValue] = useState(initial ? display(initial) : "");
  // Dirty = the user has typed since focusing. Exposed as data-dirty so the
  // selection hook knows Backspace means "edit a character", not "clear range",
  // and so the grid can flush a pending fill when another cell is clicked.
  const [dirty, setDirty] = useState(false);

  // Sync from the grid's (optimistic) value instead of remounting via
  // key={quantity}: a remount would drop focus from the anchor cell after a
  // range clear, killing the clear-then-type-a-new-value flow.
  useEffect(() => {
    setValue(initial ? display(initial) : "");
    setDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial]);

  // The anchor (dirty) cell always shows its own typing; the rest of the
  // selection echoes the preview.
  const shown = selected && !dirty && preview != null ? (preview ? display(preview) : "") : value;

  return (
    <input
      type="text"
      inputMode="numeric"
      value={shown}
      disabled={disabled}
      data-row={row}
      data-col={col}
      data-dirty={dirty ? "true" : undefined}
      onChange={(e) => {
        const digits = e.currentTarget.value.replace(/[^\d]/g, "");
        setValue(digits ? display(parseInt(digits, 10)) : "");
        setDirty(true);
        if (selected) onPreview(digits ? parseInt(digits, 10) : 0);
      }}
      onMouseDown={(e) => {
        // Shift+Click extends the selection rectangle; prevent the browser's
        // text selection so it reads as a cell range.
        if (e.shiftKey) e.preventDefault();
        onSelectMouseDown(e.shiftKey);
      }}
      onKeyDown={(e) => {
        // Enter commits (and fills the selection when one is active).
        if (e.key === "Enter") e.currentTarget.blur();
      }}
      onPaste={(e) => {
        const text = e.clipboardData.getData("text");
        // Only intercept spreadsheet data (tab/newline present); plain text
        // pastes fall through to normal in-cell editing.
        if (!/[\t\n\r]/.test(text)) return;
        e.preventDefault();
        onPasteRow(text);
      }}
      onBlur={(e) => {
        onPreview(null);
        // The grid already filled the selection from this input's value on
        // mousedown of another cell — don't commit twice.
        if (e.currentTarget.dataset.committed === "true") {
          delete e.currentTarget.dataset.committed;
          setDirty(false);
          return;
        }
        const digits = value.replace(/[^\d]/g, "");
        const v = digits ? parseInt(digits, 10) : null;
        // Commit when the user typed (even if the number is unchanged: a typed
        // 0 on a blank cell, or a cleared manual 0, looks the same but must
        // reach the grid to set or drop the manual flag), or the value
        // changed, or the last save errored (retry — otherwise a failed cell
        // whose value is already the optimistic one could never be retried).
        if (dirty || (v ?? 0) !== initial || status === "error") {
          onCommit(v, selected && dirty);
        }
        setDirty(false);
      }}
      style={{
        background: "var(--background)",
        borderColor: "var(--border)",
        color: "var(--foreground)",
        boxShadow: saveStatusRing(status) ?? (selected ? SELECTED_CELL_RING : undefined),
      }}
      className="w-full text-right text-xs border rounded-md px-1.5 py-1 outline-none transition-colors focus:border-secondary placeholder:text-muted-foreground/50"
    />
  );
}
